import { EStatus, TStatus } from 'common/types'
import {
  TStatusStage,
  TTrackingStatus,
} from 'contexts/app-context/types/app-context/components/support'

import { TimelineType } from '@interco/inter-ui/components/Timeline'

import { TServiceStatus, TTracking } from '../types/ServiceDetails.types'

export function toStringPadStart({
  fillString,
  maxLenght,
  value,
}: {
  value: string | number
  maxLenght: number
  fillString: string
}) {
  return value.toString().padStart(maxLenght, fillString)
}

export function getDate(date: Date) {
  return `${toStringPadStart({
    value: date.getUTCDate(),
    maxLenght: 2,
    fillString: '0',
  })}/${toStringPadStart({
    value: date.getUTCMonth() + 1,
    maxLenght: 2,
    fillString: '0',
  })}/${date.getUTCFullYear()}`
}

const isStatusValid = ({ trackingStatus }: { trackingStatus?: TStatusStage }) => {
  if (trackingStatus) {
    const { color, content, status } = trackingStatus
    if (color && content && status) {
      return true
    }
    return false
  }
  return false
}

const validateStatus = ({
  tracking,
  statusKey,
}: {
  tracking: TTracking
  statusKey: keyof TTrackingStatus
}) => {
  const { status } = tracking
  const trackingStatus = Object.values(status)
    .map((value) => value[statusKey])
    .filter((value) => typeof value !== 'string')

  const isValid = trackingStatus.every((value) => isStatusValid({ trackingStatus: value }))

  return isValid
}

const getStatusData = ({
  trackingStatus,
  statusKey,
}: {
  trackingStatus: TTracking
  statusKey: keyof TTrackingStatus
}) => {
  if (statusKey === 'title') return undefined

  const { open, analysis, completed } = trackingStatus.status

  const isValid = validateStatus({ tracking: trackingStatus, statusKey })

  return isValid
    ? {
        open: {
          content: open[statusKey]?.content as string,
          subContent: open[statusKey]?.subContent,
          status: open[statusKey]?.status as TimelineType,
          icon: open[statusKey]?.icon,
          color: open[statusKey]?.color as string,
        },
        waiting: {
          content: analysis[statusKey]?.content as string,
          subContent: analysis[statusKey]?.subContent,
          status: analysis[statusKey]?.status as TimelineType,
          icon: analysis[statusKey]?.icon,
          color: analysis[statusKey]?.color as string,
        },
        completed: {
          content: completed[statusKey]?.content as string,
          subContent: completed[statusKey]?.subContent,
          status: completed[statusKey]?.status as TimelineType,
          icon: completed[statusKey]?.icon,
          color: completed[statusKey]?.color as string,
        },
      }
    : undefined
}

export function getTrackStatus({
  status,
  trackingStatus,
}: {
  trackingStatus: TTracking | undefined
  status: TStatus | undefined
}): TServiceStatus | undefined {
  if (trackingStatus && status) {
    const isCompleted =
      status === EStatus.CLOSED.toUpperCase() || status === EStatus.RESOLVED.toUpperCase()
    const isWaiting = status === EStatus.PENDING.toUpperCase()
    const isAnalyzing = status === EStatus['IN PROGRESS'].toUpperCase()
    const isOpen = status === EStatus.OPEN.toUpperCase()
    const isCanceled =
      status === EStatus.ABANDONED.toUpperCase() || status === EStatus.CANCELLED.toUpperCase()
    const isAwaitingProtocol = status === EStatus.AWAITING.toUpperCase()

    if (isCompleted) return getStatusData({ trackingStatus, statusKey: 'onCompleted' })

    if (isWaiting) return getStatusData({ trackingStatus, statusKey: 'onWaiting' })

    if (isAnalyzing) return getStatusData({ trackingStatus, statusKey: 'onAnalyzing' })

    if (isOpen) return getStatusData({ trackingStatus, statusKey: 'onOpen' })

    if (isCanceled) return getStatusData({ trackingStatus, statusKey: 'onCanceled' })

    if (isAwaitingProtocol) return getStatusData({ trackingStatus, statusKey: 'onOpen' })
  }
  return undefined
}
